import { useEffect, useRef, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLUSH_INTERVAL = 10_000; // 10 seconds

function generateSessionId() {
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionMeta() {
  const ua = navigator.userAgent;
  let browser = "Unknown", os = "Unknown", device = "desktop";

  if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Edg\//.test(ua)) browser = "Edge";
  else if (/Chrome\//.test(ua)) browser = "Chrome";
  else if (/Safari\//.test(ua)) browser = "Safari";

  if (/Windows/.test(ua)) os = "Windows";
  else if (/Mac OS/.test(ua)) os = "macOS";
  else if (/Linux/.test(ua)) os = "Linux";
  else if (/Android/.test(ua)) { os = "Android"; device = "mobile"; }
  else if (/iPhone|iPad/.test(ua)) { os = "iOS"; device = "mobile"; }

  if (/Tablet|iPad/.test(ua)) device = "tablet";
  else if (/Mobile/.test(ua)) device = "mobile";

  return {
    user_agent: ua,
    browser,
    os,
    device_type: device,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    page_url: window.location.href,
  };
}

/**
 * useActivityLogger — comprehensive activity tracking for assessments.
 *
 * Captures:  page load, question views, responses, voice recordings,
 *            file opens, test runs, console commands, NOTES edits,
 *            timer events, submit clicks, certification, refreshes,
 *            connection drops/reconnects.
 *
 * Events are batched and flushed every 10 s or on page unload via sendBeacon.
 */
export function useActivityLogger(assessmentToken, active = true) {
  const sessionId = useRef(generateSessionId());
  const buffer = useRef([]);
  const meta = useRef(null);
  const refreshCount = useRef(0);
  const mountTime = useRef(Date.now());

  // Resolve session metadata once
  if (!meta.current) meta.current = getSessionMeta();

  // ── Core: push an event into the buffer ──
  const logEvent = useCallback(
    (eventType, eventName, detail = {}, sectionId = null, questionId = null) => {
      if (!active) return;
      buffer.current.push({
        event_type: eventType,
        event_name: eventName,
        timestamp: new Date().toISOString(),
        section_id: sectionId,
        question_id: questionId,
        detail,
      });
    },
    [active]
  );

  // ── Flush buffer to backend ──
  const flush = useCallback(async () => {
    if (!assessmentToken || buffer.current.length === 0) return;
    const events = [...buffer.current];
    buffer.current = [];
    const payload = JSON.stringify({
      session_id: sessionId.current,
      assessment_token: assessmentToken,
      events,
      ...meta.current,
    });
    try {
      await fetch(`${API}/analytics/activity/batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      });
    } catch {
      // Re-queue on failure
      buffer.current = [...events, ...buffer.current];
    }
  }, [assessmentToken]);

  // ── sendBeacon fallback for unload ──
  const beaconFlush = useCallback(() => {
    if (!assessmentToken || buffer.current.length === 0) return;
    const payload = JSON.stringify({
      session_id: sessionId.current,
      assessment_token: assessmentToken,
      events: buffer.current,
      ...meta.current,
    });
    buffer.current = [];
    navigator.sendBeacon(
      `${API}/analytics/activity/batch`,
      new Blob([payload], { type: "application/json" })
    );
  }, [assessmentToken]);

  // ── Lifecycle: page load, refresh, online/offline, periodic flush ──
  useEffect(() => {
    if (!active || !assessmentToken) return;

    // Detect page refresh vs first load
    const navEntries = performance.getEntriesByType("navigation");
    const isReload = navEntries.length > 0 && navEntries[0].type === "reload";
    if (isReload) refreshCount.current += 1;

    logEvent("system", "assessment_opened", {
      is_reload: isReload,
      refresh_count: refreshCount.current,
      ...meta.current,
    });

    // Connection monitoring
    const handleOffline = () =>
      logEvent("system", "connection_lost", { at: new Date().toISOString() });
    const handleOnline = () =>
      logEvent("system", "connection_restored", { at: new Date().toISOString() });

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    window.addEventListener("beforeunload", beaconFlush);
    window.addEventListener("pagehide", beaconFlush);

    // Listen for voice-activity events from VoiceRecorder
    const handleVoiceActivity = (e) => {
      const { action, questionId, sectionId, ...rest } = e.detail || {};
      logEvent("voice", `voice_${action}`, rest, sectionId, questionId);
    };
    window.addEventListener("voice-activity", handleVoiceActivity);

    const timer = setInterval(flush, FLUSH_INTERVAL);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("beforeunload", beaconFlush);
      window.removeEventListener("pagehide", beaconFlush);
      window.removeEventListener("voice-activity", handleVoiceActivity);
      clearInterval(timer);
      // Final async flush on unmount
      flush();
    };
  }, [active, assessmentToken, logEvent, flush, beaconFlush]);

  // ── Convenience helpers (all delegate to logEvent) ──

  const logQuestionViewed = useCallback(
    (sectionId, questionId, questionTitle) =>
      logEvent("navigation", "question_viewed", { question_title: questionTitle }, sectionId, questionId),
    [logEvent]
  );

  const logResponseChange = useCallback(
    (sectionId, questionId, charCount) =>
      logEvent("response", "response_typed", { char_count: charCount }, sectionId, questionId),
    [logEvent]
  );

  const logVoiceRecording = useCallback(
    (action, sectionId, questionId, detail = {}) =>
      logEvent("voice", `voice_${action}`, detail, sectionId, questionId),
    [logEvent]
  );

  const logFileOpened = useCallback(
    (filePath) => logEvent("sandbox", "file_opened", { file_path: filePath }),
    [logEvent]
  );

  const logTestRun = useCallback(
    (detail = {}) => logEvent("sandbox", "test_run_triggered", detail),
    [logEvent]
  );

  const logConsoleCommand = useCallback(
    (command) => logEvent("sandbox", "console_command", { command }),
    [logEvent]
  );

  const logNotesEdit = useCallback(
    (charCount) => logEvent("sandbox", "notes_edited", { char_count: charCount }),
    [logEvent]
  );

  const logTimerEvent = useCallback(
    (action, sectionId, detail = {}) =>
      logEvent("timer", `timer_${action}`, detail, sectionId),
    [logEvent]
  );

  const logPhaseSubmit = useCallback(
    (sectionId, phaseIndex, method) =>
      logEvent("submission", "phase_submitted", { phase_index: phaseIndex, method }, sectionId),
    [logEvent]
  );

  const logCertification = useCallback(
    (action, detail = {}) => logEvent("certification", `certification_${action}`, detail),
    [logEvent]
  );

  const logFinalSubmit = useCallback(
    () => {
      logEvent("submission", "assessment_submitted", {
        session_duration_ms: Date.now() - mountTime.current,
      });
      // Immediate flush on submit
      flush();
    },
    [logEvent, flush]
  );

  return {
    logEvent,
    logQuestionViewed,
    logResponseChange,
    logVoiceRecording,
    logFileOpened,
    logTestRun,
    logConsoleCommand,
    logNotesEdit,
    logTimerEvent,
    logPhaseSubmit,
    logCertification,
    logFinalSubmit,
    flush,
  };
}
